import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Check, X, AlertCircle, Loader2, HandHeart, MapPin } from 'lucide-react';
import { TeamAApi, ApiError, type LostItem } from '@ai-tiaozs/shared';
import { SafeImage } from '@/components/SafeImage';

/**
 * A 队 · ③ 自助领取(信任型)
 *
 * 失主到失物点后,自己核对是不是自己的物品。
 * - 确认是 → "我已取回" → 状态变 returned
 * - 不是 → "没有找到 / 不是我的" → 不改 item 状态
 *
 * 系统不判定谁是主人,失主对自己诚信负责。
 */
export function TeamAPickupIndex() {
  const [items, setItems] = useState<LostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    TeamAApi.listItems({ group: 'available' })
      .then((d) => setItems(d.items))
      .catch((e) => setError(e instanceof ApiError ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-bold">③ 自助领取</h1>
        <p className="text-xs text-slate-500">先选择疑似物品,再到固定领取地点实物核对</p>
      </header>

      <section className="card space-y-2">
        <h2 className="flex items-center gap-1 text-sm font-semibold text-teamA-primary">
          <HandHeart className="h-4 w-4" /> 选择要核对的失物
        </h2>
        <p className="text-xs text-slate-600">
          如果你是从“智能寻找”结果进入,系统会直接打开具体物品。这里用于手动浏览等待领取的失物。
        </p>
      </section>

      <section className="card space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">等待领取的失物</h2>
          <span className="text-[11px] text-slate-400">{items.length} 件</span>
        </div>

        {loading && (
          <p className="flex items-center gap-1 text-xs text-slate-500">
            <Loader2 className="h-3 w-3 animate-spin" /> 加载中…
          </p>
        )}
        {error && <p className="text-xs text-teamA-danger">{error}</p>}
        {!loading && !error && items.length === 0 && (
          <div className="rounded-md bg-slate-50 px-3 py-4 text-center text-sm text-slate-500">
            暂时没有等待领取的失物
          </div>
        )}

        {items.map((it) => (
          <Link
            key={it.id}
            to={`/pickup/${it.id}`}
            className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3 hover:shadow"
          >
            <SafeImage
              team="a"
              imageUrl={it.imageUrl}
              imageKey={it.imageKey}
              alt={it.title}
            />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-1">
                <span className="badge bg-slate-200 text-slate-700">{it.category}</span>
                <span className="badge bg-teamA-warning/10 text-teamA-warning">等待领取</span>
              </div>
              <p className="mt-1 text-sm font-semibold">{it.title}</p>
              {it.storageLocation && (
                <p className="text-[11px] text-slate-500">
                  <MapPin className="mr-1 inline h-3 w-3" />
                  领取地点 {it.storageLocation}
                </p>
              )}
            </div>
          </Link>
        ))}
      </section>
    </div>
  );
}

export function TeamAPickup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<LostItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false); // 二次确认
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [feedbackMode, setFeedbackMode] = useState<'not_found' | 'not_mine' | null>(null);
  const [feedbackSending, setFeedbackSending] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    TeamAApi.getItem(id)
      .then(setItem)
      .catch((e) => setError(e instanceof ApiError ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, [id]);

  const confirmPickup = async () => {
    if (!item) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await TeamAApi.pickup({ itemId: item.id, confirmed: true });
      setItem({ ...item, status: r.itemStatus as 'returned' });
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '提交失败');
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  const submitFeedback = async () => {
    if (!item || !feedbackMode) return;
    setFeedbackSending(true);
    setError(null);
    try {
      await TeamAApi.feedback({
        searchId: 'manual', // 走详情页直接进来,无 searchId
        selectedItemId: item.id,
        feedback: feedbackMode,
      });
      setFeedbackMode(null);
      navigate('/');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '反馈失败');
    } finally {
      setFeedbackSending(false);
    }
  };

  if (loading) return <p className="flex items-center gap-1 text-xs text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> 加载中…</p>;
  if (error && !item) return <p className="text-xs text-teamA-danger">{error}</p>;
  if (!item) return <p className="text-xs text-slate-500">物品不存在或已下架</p>;

  const isReturned = item.status === 'returned';
  const isAvailable = item.status === 'available';

  return (
    <div className="space-y-4">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-teamA-primary">
        ← 返回失物墙
      </Link>

      <header>
        <h1 className="text-lg font-bold">③ 自助领取</h1>
        <p className="text-xs text-slate-500">到失物点核对,只领取属于自己的物品</p>
      </header>

      {/* 诚信使用提示 */}
      <div className="rounded-md border border-teamA-primary/20 bg-teamA-primary/5 px-3 py-2 text-[11px] text-slate-700 space-y-1">
        <p className="font-semibold text-teamA-primary">📢 诚信使用说明</p>
        <p>本系统基于同学互助与诚信使用。请只领取<strong>确认属于自己</strong>的物品。</p>
        <p>取回后请点【我已取回】,让失物墙及时更新。</p>
      </div>

      {/* 完成态 */}
      {done && isReturned && (
        <section className="card border-teamA-safe/30 bg-teamA-safe/5">
          <h2 className="flex items-center gap-1 text-base font-semibold text-teamA-safe">
            <Check className="h-5 w-5" /> 已找回 🎉
          </h2>
          <p className="mt-1 text-xs text-slate-600">物品已标记为已归还,感谢配合校园互助。</p>
          <Link to="/" className="btn-primary mt-3 w-full">返回失物墙</Link>
        </section>
      )}

      {/* 物品公开信息 */}
      {!done && (
        <section className="card space-y-3">
          <div className="flex items-center gap-3">
            <SafeImage
              team="a"
              imageUrl={item.imageUrl}
              imageKey={item.imageKey}
              alt={item.title}
            />
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <span className="badge bg-slate-200 text-slate-700">{item.category}</span>
                {isAvailable && <span className="badge bg-teamA-warning/10 text-teamA-warning">等待寻找</span>}
                {isReturned && <span className="badge bg-teamA-safe/10 text-teamA-safe">已找回</span>}
              </div>
              <p className="mt-1 text-sm font-semibold">{item.title}</p>
              {item.foundLocation && (
                <p className="text-[11px] text-slate-500">捡到于 {item.foundLocation}</p>
              )}
            </div>
          </div>

          {/* 公开特征 */}
          {item.features && (
            <div className="rounded-md bg-slate-50 p-2 text-xs text-slate-700">
              {[
                item.features.color, item.features.shape, item.features.pattern,
                item.features.text_mark, item.features.size,
              ].filter(Boolean).map((v, i) => (
                <span key={i} className="mr-1 inline-block rounded bg-white px-2 py-0.5">{v}</span>
              ))}
            </div>
          )}

          {/* 固定存放地点 — 醒目 */}
          {item.storageLocation && (
            <div className="rounded-md border border-teamA-primary/30 bg-teamA-primary/5 p-3">
              <p className="text-[11px] text-slate-500">📍 固定领取地点</p>
              <p className="mt-1 text-base font-semibold text-teamA-primary">
                {item.storageLocation}
              </p>
              <p className="mt-1 text-[11px] text-slate-500">请到上述地点实地查看,核对是否为你丢失的物品</p>
            </div>
          )}
        </section>
      )}

      {/* 操作按钮组 */}
      {!done && isAvailable && (
        <section className="space-y-2">
          {/* 我已取回 — 主操作(二次确认) */}
          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              className="btn-primary w-full bg-teamA-safe hover:bg-teamA-safe/90"
            >
              <Check className="mr-1 h-4 w-4" /> 我已取回
            </button>
          ) : (
            <div className="card border-teamA-safe/30 bg-teamA-safe/5 space-y-2">
              <p className="flex items-center gap-1 text-sm font-semibold text-teamA-safe">
                <AlertCircle className="h-4 w-4" /> 二次确认
              </p>
              <p className="text-xs text-slate-700">
                请确认你<strong>已经在 {item.storageLocation} 实际取回</strong>了属于自己的物品。
              </p>
              <p className="text-[11px] text-slate-500">
                点击确认后,系统会公开标记该物品为"已找回",其他人将不再看到。
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setConfirming(false)}
                  disabled={submitting}
                  className="btn-outline"
                >
                  取消
                </button>
                <button
                  onClick={confirmPickup}
                  disabled={submitting}
                  className="btn-primary bg-teamA-safe hover:bg-teamA-safe/90"
                >
                  {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
                  确认已取回
                </button>
              </div>
            </div>
          )}

          {/* 反馈按钮 */}
          {!feedbackMode ? (
            <div className="card space-y-2 bg-slate-50">
              <p className="text-xs text-slate-600">
                不是你的物品,或者没有找到?
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setFeedbackMode('not_found')}
                  disabled={feedbackSending}
                  className="btn-outline text-xs"
                >
                  <X className="mr-1 inline h-3 w-3" /> 没有找到
                </button>
                <button
                  onClick={() => setFeedbackMode('not_mine')}
                  disabled={feedbackSending}
                  className="btn-outline text-xs"
                >
                  <X className="mr-1 inline h-3 w-3" /> 不是我的
                </button>
              </div>
              <p className="text-[11px] text-slate-500">
                反馈后会记录一次匹配未成功,用于改进 AI 寻找能力
              </p>
            </div>
          ) : (
            <div className="card border-teamA-warning/30 bg-teamA-warning/5 space-y-2">
              <p className="text-sm font-semibold text-teamA-warning">
                确认反馈「{feedbackMode === 'not_found' ? '没有找到' : '不是我的'}」?
              </p>
              <p className="text-xs text-slate-600">物品状态不会改变,会留作 AI 改进参考</p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setFeedbackMode(null)} className="btn-outline">取消</button>
                <button
                  onClick={submitFeedback}
                  disabled={feedbackSending}
                  className="btn-primary"
                >
                  {feedbackSending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                  确认
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {isReturned && !done && (
        <section className="card border-teamA-safe/30 bg-teamA-safe/5">
          <p className="text-sm font-semibold text-teamA-safe">该物品已被标记为已找回</p>
          <p className="mt-1 text-xs text-slate-600">失物墙已更新,谢谢配合。</p>
        </section>
      )}

      {error && <p className="text-xs text-teamA-danger">{error}</p>}

      {/* 底部说明 */}
      <p className="rounded-md bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
        💡 系统不会判断物品归属,失主对自己取走的物品负责。如有争议请联系失物点管理员。
      </p>
    </div>
  );
}
